import { useQuery } from '@tanstack/react-query';

import { QueryError } from '@/models/ErrorResponse';
import { ProjectType } from '@/models/ProjectType';
import { monolithApi } from '@/utils/api';
import { ownedAppsListPath } from '@/utils/monolithApiRouteService';

type OwnedAppListItem = {
  slug: string;
  title: string;
  isPublic: boolean;
  projectType: ProjectType;
  avatarIconUrl: string | null;
};

export type OwnedAppsList = {
  apps: OwnedAppListItem[];
  /** True when more projects exist than were returned — either the caller's limit or the server cap. */
  truncated: boolean;
};

type Entity = { accountType: 'User' | 'Organization'; slug: string };
type Options = { limit?: number; enabled?: boolean };

const useOwnedAppsList = (entity: Entity, { limit, enabled = false }: Options = {}) =>
  useQuery<OwnedAppsList, QueryError>({
    queryKey: ['owned-apps-list', entity.accountType, entity.slug, limit ?? null],
    queryFn: async () => {
      try {
        const response = await monolithApi.get<OwnedAppsList>(
          ownedAppsListPath(entity.accountType, entity.slug, limit),
        );
        return response.data;
      } catch (error: any) {
        throw new QueryError(error);
      }
    },
    // The project set can only change through a full page load in the Monolith.
    staleTime: Infinity,
    enabled,
  });

export { useOwnedAppsList };
